<template>
    <b-modal
        id="resource-picker-modal"
        v-model="shown"
        scrollable
        title="Add Resources"
        size="xl"
        lazy
        @hide="reloadCostableOnModalClose"
    >

        <div class="row mb-3">
            <div class="col-3">
                <b-form-group class="mb-0">
                    <div>
                        <label>Types</label>
                    </div>
                    <Select
                        v-model="filters.type"
                        placeholder="Select a type..."
                        :options="resourceTypeOptions"
                        @input="debouncedGetResources({ page: page })"
                    />
                </b-form-group>
            </div>

            <div class="col-2">
                <b-form-group class="mb-0">
                    <div>
                        <label>Supplier</label>
                    </div>
                    <AccountSearchSelect
                        v-model="filters.supplier"
                        placeholder="Select a supplier..."
                        :taggable="false"
                        only-with-resources
                        @input="debouncedGetResources({ page: page })"
                    />
                </b-form-group>
            </div>
            <div class="col-2">
                <b-form-group class="mb-0">
                    <div>
                        <label>Parent Resources</label>
                    </div>
                    <b-input
                        v-model.trim="filters.parent_resource"
                        placeholder="Enter a parent resource"
                        @input="debouncedGetResources({ page: page })"
                    />
                </b-form-group>
            </div>

            <div class="col-4">
                <b-form-group class="mb-0">
                    <div>
                        <label>Search</label>
                    </div>
                    <b-input
                        v-model.trim="filters.search"
                        placeholder="Enter a query..."
                        @input="debouncedGetResources({ page: page })"
                    />
                </b-form-group>
            </div>

            <div class="col-1">
                <b-form-group class="mb-0">
                    <div>
                        <label>&nbsp;</label>
                    </div>
                    <button
                        v-b-tooltip.hover
                        class="btn btn-block btn-warning"
                        title="Reset filters"
                        @click="resetFilters"
                    >
                        <i class="fa fa-times" />
                    </button>
                </b-form-group>
            </div>

        </div>

        <div
            v-if="allocationsCount"
            class="row mb-3"
        >
            <div class="col-12">
                <a
                    v-if="!filters.allocated"
                    href="#"
                    @click="displayAllocatedResources"
                >View {{ allocationsCount }} selected resources</a>
                <a
                    v-else
                    href="#"
                    @click="resetFilters"
                >View all resources</a>
            </div>
        </div>

        <Spinner v-show="loading" />
        <table
            v-show="!loading"
            class="table table-sm mb-0"
        >
            <thead>
                <tr>
                    <th />
                    <th>
                        Type
                    </th>
                    <th @click="setSort('name')">
                        <a href="#">
                            Name
                            <i
                                v-if="filters.sort === 'name'"
                                :class="`fa fa-fw fa-${filters.direction === 'asc' ? 'caret-down' : 'caret-up'}`"
                            />
                        </a>
                    </th>
                    <th>
                        Supplier
                    </th>
                    <th>Variants</th>
                    <th>
                        Quantity
                    </th>
                    <th
                        class="text-right"
                        @click="setSort('unit_cost')"
                    >
                        <a href="#">
                            Unit Cost
                            <i
                                v-if="filters.sort === 'unit_cost'"
                                :class="`fa fa-fw fa-${filters.direction === 'asc' ? 'caret-down' : 'caret-up'}`"
                            />
                        </a>
                    </th>
                    <th
                        class="text-right"
                    >
                        Total Cost
                    </th>
                    <th
                        class="text-right"
                        @click="setSort('unit_price')"
                    >
                        <a href="#">
                            Unit Price
                            <i
                                v-if="filters.sort === 'unit_price'"
                                :class="`fa fa-fw fa-${filters.direction === 'asc' ? 'caret-down' : 'caret-up'}`"
                            />
                        </a>
                    </th>
                    <th
                        class="text-right"
                    >
                        Total Price
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="resource in resources"
                    :key="resource.id"
                    :class="{ 'success': resource.allocation_count }"
                >

                    <td>
                        <img
                            v-if="resource.image_url"
                            :src="resource.image_url"
                            alt=""
                            class="img-profile img-circle"
                        >
                    </td>

                    <!-- Type -->
                    <td>{{ resource.type.label }}</td>

                    <!-- Name -->
                    <td>
                        <a
                            :href="`/admin/crm/resources/${resource.id}`"
                            target="_blank"
                        > <b-badge
                              v-if="resource.archived_at"
                              variant="warning"
                          >Archived</b-badge>
                            {{ resource.label }}
                            <small v-if="resource.description"><br>{{ resource.description }}</small>
                        </a>
                    </td>

                    <!-- Supplier -->
                    <td>{{ resource.supplier.label }}</td>

                    <td width="20%">
                        <CostableResourceVariantPicker
                            :resource="resource"
                            @change="debouncedUpdateValue(resource.id, $event, 'selected_variants', false)"
                        />
                    </td>

                    <!-- Quantity -->
                    <td
                        width="10%"
                        class="text-right"
                    >
                        <b-input
                            :value="resource.quantity"
                            size="sm"
                            type="text"
                            @input="debouncedUpdateValue(resource.id, $event, 'quantity')"
                        />
                    </td>

                    <!-- Unit Cost -->
                    <td width="10%">
                        <b-input
                            :value="resource.unit_cost"
                            size="sm"
                            type="text"
                            @input="debouncedUpdateValue(resource.id, $event, 'unit_cost')"
                        />
                    </td>

                    <!-- Total Cost -->
                    <td
                        width="10%"
                        class="text-right"
                    >
                        {{ resource.total_cost }}
                    </td>

                    <!-- Unit Price -->
                    <td width="10%">
                        <b-input
                            :value="resource.unit_price"
                            size="sm"
                            type="text"
                            @input="debouncedUpdateValue(resource.id, $event, 'unit_price')"
                        />
                    </td>

                    <!-- Total Price -->
                    <td
                        width="10%"
                        class="text-right"
                    >
                        {{ resource.total_price }}
                    </td>

                </tr>
            </tbody>
            <tfoot>
                <tr>
                    <th colspan="3" />
                    <th>Totals:</th>
                    <th>{{ totalQuantity }}</th>
                    <th />
                    <th class="text-right">
                        {{ totalCost }}
                    </th>
                    <th />
                    <th class="text-right">
                        {{ totalPrice }}
                    </th>
                </tr>
            </tfoot>
        </table>

        <div class="mt-3 d-flex justify-content-between">
            <div class="d-flex align-items-center">
                <span>{{ totalItems }} resources found</span>
            </div>
            <div class="d-flex align-items-center">
                <span class="mr-3">{{ `${page}/${lastPage}` }}</span>
                <b-btn
                    :disabled="page === 1"
                    variant="default"
                    size="sm"
                    class="mr-2"
                    @click="previousPage()"
                >
                    Previous
                </b-btn>
                <b-btn
                    :disabled="lastPage === page"
                    variant="default"
                    size="sm"
                    @click="nextPage()"
                >
                    Next
                </b-btn>
            </div>
        </div>

        <div slot="modal-footer">
            <b-btn
                variant="default"
                class="mr-2"
                @click="close()"
            >
                Close
            </b-btn>
        </div>

    </b-modal>
</template>

<script>
import vueComponentReset from '@ianwalter/vue-component-reset';
import debounce from 'debounce';
import numeral from 'numeral';
import { formatCurrency } from '@/utils/helpers';
import CostableResourceVariantPicker from './CostableResourceVariantPicker';

const resetMixin = vueComponentReset(() => ({
    shown: false,
    loading: false,
    costId: null,
    defaultResourceTypeId: null,
    resourceTypeOptions: [],
    resources: [],
    filters: {
        search: '',
        type: null,
        supplier: null,
        parentResource: null,
        allocated: false,
        showArchived: 0,
        sort: 'name',
        direction: 'asc',
    },
    page: 1,
    lastPage: 1,
    totalItems: 0,
    allocationsCount: 0,
    totalQuantity: 0,
    totalCost: 0,
    totalPrice: 0,
    hasChanges: false,
}));

export default {

    components: {
        CostableResourceVariantPicker,
    },

    mixins: [
        resetMixin,
    ],

    computed: {
        costableType() {
            return this.$store.getters['costable/costableType'];
        },
        costableId() {
            return this.$store.getters['costable/costableId'];
        },
    },

    created() {
        this.$eventBus.$on(this.$events.openCostResourcePickerModal, this.onOpen);
    },

    methods: {
        formatCurrency,
        debouncedUpdateValue: debounce(function _(resourceId, value, attribute, numeric = true) {
            return this.updateValue(resourceId, value, attribute, numeric);
        }, 1000),
        async updateValue(resourceId, value, attribute, numeric = true) {
            this.hasChanges = true;
            await this.$store.dispatch('costable/patchCostResourceAllocation', {
                costId: this.costId,
                resourceId,
                data: {
                    [attribute]: numeric ? numeral(value).value() : value,
                },
            });
            await this.debouncedGetResources({ page: this.page, shouldSetLoading: false });
        },
        debouncedGetResources: debounce(function _({ page, shouldSetLoading = true }) {
            return this.getResources({ page, shouldSetLoading });
        }, 500),
        async getResources({ page, shouldSetLoading = true, useDefaultTypeId = false }) {
            this.loading = shouldSetLoading;
            const {
                resources,
                last_page: lastPage,
                total_items: totalItems,
                allocations_count: allocationsCount,
                total_quantity: totalQuantity,
                total_cost: totalCost,
                total_price: totalPrice,
                default_resource_type_id: defaultResourceTypeId,
            } = await this.$store.dispatch('costable/getCostResourceAllocations',
                {
                    costId: this.costId,
                    filters: {
                        typeId: this.filters.type ? this.filters.type.id : null,
                        supplierId: this.filters.supplier ? this.filters.supplier.id : null,
                        parentResource: this.filters.parent_resource,
                        searchQuery: this.filters.search,
                        onlyAllocated: this.filters.allocated,
                        showArchived: this.filters.showArchived,
                        useDefaultTypeId,
                        sort: this.filters.sort,
                        direction: this.filters.direction,
                    },
                    page,
                });

            this.resourceTypeOptions = await this.$store.cache.dispatch('selects/getResourceTypes', {
                supplierId: this.filters.supplier ? this.filters.supplier.id : '',
            });
            this.resources = resources;
            this.page = page;
            this.lastPage = lastPage;
            this.totalItems = totalItems;
            this.allocationsCount = allocationsCount;
            this.totalQuantity = totalQuantity;
            this.totalCost = totalCost;
            this.totalPrice = totalPrice;
            this.defaultResourceTypeId = defaultResourceTypeId;
            this.loading = false;
        },
        open() {
            this.shown = true;
        },
        close() {
            this.shown = false;
        },
        reloadCostableOnModalClose() {
            if (this.hasChanges) {
                this.$store.dispatch('costable/reloadCostable');
            }
        },
        previousPage() {
            this.getResources({ page: this.page - 1 });
        },
        nextPage() {
            this.getResources({ page: this.page + 1 });
        },
        resetFilters(shouldGetResources = true) {
            this.filters = {
                search: '',
                type: null,
                supplier: null,
                allocated: null,
                showArchived: 0,
                sort: 'name',
                direction: 'asc',
            };
            this.filters.showArchived = 0;
            shouldGetResources && this.getResources({ page: 1 });
        },
        displayAllocatedResources() {
            this.resetFilters(false);
            this.filters.allocated = true;
            this.filters.showArchived = 1;
            this.getResources({ page: this.page });
        },
        async onOpen({ costId }) {
            this.reset();
            this.costId = costId;
            this.open();
            // TODO we could have a nice loading spinner here while all this stuff happens
            this.resourceTypeOptions = await this.$store.dispatch('selects/getResourceTypes', {
                supplierId: '',
            });
            this.filters.showArchived = 0;
            await this.getResources({ page: 1, useDefaultTypeId: true });
            if (this.defaultResourceTypeId) {
                this.filters.type = this.resourceTypeOptions.find(type => type.id === this.defaultResourceTypeId);
            }
            if (this.allocationsCount) {
                this.displayAllocatedResources();
            }
        },
        setSort(sort) {
            if (this.filters.sort === sort) {
                this.filters.direction = this.filters.direction === 'asc' ? 'desc' : 'asc';
            } else {
                this.filters.sort = sort;
                this.filters.direction = 'asc';
            }
            this.getResources({ page: this.page });
        },
    },

};
</script>
